/**
 * CockpitKPIStrip — 4 KPI business cards for the Chutt... agency cockpit
 * Vertical layout (prototype C1): icon top, label, value, subtext.
 * accentColor (agency color) = border-top hover + mouse glow + icon tints.
 */
import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TagIcon from '@mui/icons-material/Tag';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors } from '../../styles/colors';

function hexToRgba(hex, alpha) {
  if (!hex || hex.length < 7) return `rgba(200,145,58,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function KPICard({ icon: IconComp, label, value, sub, subColor, iconColor, iconBg, accentColor, delay }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    cardRef.current.style.setProperty('--mx', `${x}%`);
    cardRef.current.style.setProperty('--my', `${y}%`);
  };

  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      sx={{
        background: colors.cardGlass,
        backdropFilter: 'blur(16px)',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: '20px',
        p: '26px 28px',
        borderTop: '3px solid transparent',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s',
        animation: `fadeInUp 0.5s ease ${delay}s both`,
        '@keyframes fadeInUp': {
          '0%': { opacity: 0, transform: 'translateY(12px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          borderColor: colors.primaryLight,
          borderTopColor: accentColor,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(300px circle at var(--mx, 50%) var(--my, 50%), ${hexToRgba(accentColor, 0.06)} 0%, transparent 50%)`,
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 0.3s',
        },
        '&:hover::before': {
          opacity: 1,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '14px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '14px',
        }}
      >
        <IconComp sx={{ fontSize: '1.3rem', color: iconColor }} />
      </Box>

      <Typography
        sx={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.78rem',
          color: colors.textMed,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          fontWeight: 600,
          mb: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: '2.6rem',
          fontWeight: 500,
          color: colors.charcoal,
          lineHeight: 1.1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>

      <Typography
        sx={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.78rem',
          fontWeight: 400,
          color: subColor || colors.textLight,
          mt: 0.75,
          lineHeight: 1.4,
        }}
      >
        {sub}
      </Typography>
    </Box>
  );
}

function CockpitKPIStrip({ kpis, accentColor }) {
  if (!kpis) return null;

  const accent = accentColor || '#C8913A';
  const hasCrisis = kpis.crises_active > 0;

  const cards = [
    {
      icon: AttachMoneyIcon,
      label: 'Revenu mensuel',
      iconColor: accent,
      iconBg: hexToRgba(accent, 0.08),
      value: `${(kpis.revenue_monthly || 0).toLocaleString('fr-FR')} EUR`,
      sub: `${kpis.brands_count} marque${kpis.brands_count > 1 ? 's' : ''} x ${kpis.avg_price} EUR`,
    },
    {
      icon: TagIcon,
      label: 'Marques couvertes',
      iconColor: colors.info,
      iconBg: colors.infoBg,
      value: `${kpis.brands_count} / ${kpis.brands_max || 10}`,
      sub: `Abo moyen ${kpis.avg_price} EUR/mois`,
    },
    {
      icon: WarningAmberIcon,
      label: 'Crises actives',
      iconColor: hasCrisis ? colors.danger : colors.success,
      iconBg: hasCrisis ? colors.dangerBg : colors.successBg,
      value: `${kpis.crises_active}`,
      sub: hasCrisis ? (kpis.crises_brands || []).join(', ') : 'Aucune crise',
      subColor: hasCrisis ? colors.danger : colors.success,
    },
    {
      icon: CheckCircleOutlineIcon,
      label: 'Actions realisees',
      iconColor: accent,
      iconBg: hexToRgba(accent, 0.12),
      value: `${kpis.actions_completed} / ${kpis.actions_total}`,
      sub: `${kpis.actions_pct}% completees`,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 3,
        mb: 4,
      }}
    >
      {cards.map((card, index) => (
        <KPICard key={index} {...card} accentColor={accent} delay={0.08 * index} />
      ))}
    </Box>
  );
}

export default CockpitKPIStrip;
